import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Images } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXPORT_FPS = 30;

export default function ExportFramesDialog({
  open,
  onOpenChange,
  fileId,
  fileName,
  selectedFrames,
  seriesSliceIdx,
}) {
  const frames = [...(selectedFrames || [])].sort((a, b) => a - b);
  const [format, setFormat] = useState("zip");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setFormat("zip");
  }, [open]);

  const handleExport = async () => {
    if (!fileId || frames.length === 0) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/dicom/${fileId}/export-frames`,
        {
          frames,
          format,
          fps: EXPORT_FPS,
          slice_idx: seriesSliceIdx ?? null,
        },
        { responseType: "blob" }
      );
      const mime = format === "zip"
        ? "application/zip"
        : format === "avi"
          ? "video/x-msvideo"
          : "video/mp4";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const base = (fileName || "file").replace(/\.dcm$/i, "");
      const ext = format === "zip" ? "zip" : format;
      a.download = `${base}_${frames.length}frames.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Extracted ${frames.length} frame(s) as ${format.toUpperCase()}`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Frame extraction failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-frames-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Images className="w-4 h-4 text-primary" /> Extract selected frames
          </DialogTitle>
          <DialogDescription className="text-xs">
            Export {frames.length} selected frame{frames.length !== 1 ? "s" : ""} as PNG images (ZIP) or a short video.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Format</Label>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: "zip", label: "ZIP (PNG)", note: "One PNG per frame" },
                { v: "mp4", label: "MP4", note: "Frames in selection order" },
                { v: "avi", label: "AVI", note: "MJPEG video" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFormat(opt.v)}
                  data-testid={`extract-format-${opt.v}`}
                  className={`flex-1 min-w-[90px] text-left p-2.5 rounded-md border text-xs transition-colors ${
                    format === opt.v
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-border hover:bg-secondary/40 text-muted-foreground"
                  }`}
                >
                  <div className="font-semibold">{opt.label}</div>
                  <div className="text-[10px] mt-0.5 opacity-70">{opt.note}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-muted-foreground border-t pt-3">
            Frames:{" "}
            <strong className="text-foreground font-mono">
              {frames.length <= 12
                ? frames.map((f) => f + 1).join(", ")
                : `${frames.slice(0, 8).map((f) => f + 1).join(", ")} … +${frames.length - 8} more`}
            </strong>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={busy || frames.length === 0} data-testid="extract-confirm">
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Extracting…</> : "Extract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
